import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { PHASES, GENDER_QUESTION } from "../../data/assessment";
import { MALE_QUESTIONS } from "../../data/male_assessment";
import ProgressBar from "../../components/ProgressBar";
import QuestionCard from "../../components/QuestionCard";
import InsightScreen from "../../components/InsightScreen";
import { Button } from "../../components/ui/button";
import { Check, Scissors, ShieldCheck } from "lucide-react";
import { Checkbox } from "../../components/ui/checkbox";

// Restrict gender picker to Woman + Man only (Salon flow)
const SALON_GENDER = {
  ...GENDER_QUESTION,
  title: {
    en: "Select the assessment for your client",
    it: "Seleziona l'analisi per il tuo cliente",
  },
  helper: {
    en: "No client name, email or personal data is collected.",
    it: "Nessun nome, email o dato personale del cliente viene raccolto.",
  },
  options: GENDER_QUESTION.options.filter((o) => o.id === "woman" || o.id === "man"),
};

const SalonHeader = ({ language, setLanguage }) => (
  <div className="fixed top-3 right-4 z-40 flex items-center gap-2">
    <select
      value={language}
      onChange={(e) => setLanguage(e.target.value)}
      data-testid="salon-lang-select"
      className="text-[10px] kt-tracking uppercase bg-white/90 border kt-line rounded-full px-3 py-1.5 text-[#383633]"
    >
      <option value="en">EN</option>
      <option value="it">IT</option>
    </select>
  </div>
);

// -------------------- Female path (reuses phases from data/assessment.js)
function FemaleFlow({ language, onDone }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showInsight, setShowInsight] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const phase = PHASES[phaseIdx];
  const currentQ = phase?.questions[qIdx];

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/salons/anonymous-assessment`, {
        flow: "female",
        language,
        answers: finalAnswers,
        confirm_adult_and_no_pregnancy: true,
      });
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compute result");
      setSubmitting(false);
    }
  };

  const advance = (next) => {
    if (qIdx < phase.questions.length - 1) setQIdx(qIdx + 1);
    else if (phaseIdx < PHASES.length - 1) setShowInsight(true);
    else submit(next);
  };

  const select = (optId) => {
    const next = { ...answers, [currentQ.id]: optId };
    setAnswers(next);
    setTimeout(() => advance(next), 220);
  };

  const back = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
    else if (phaseIdx > 0) {
      const prev = PHASES[phaseIdx - 1];
      setPhaseIdx(phaseIdx - 1);
      setQIdx(prev.questions.length - 1);
    }
  };

  return (
    <div>
      <ProgressBar
        phaseIndex={phaseIdx}
        totalPhases={PHASES.length}
        questionIndex={qIdx}
        totalInPhase={phase?.questions.length || 1}
      />
      <div className="max-w-md mx-auto px-6 pt-24 pb-40">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
          {phase?.title[language] || phase?.title.en}
        </div>
        <AnimatePresence mode="wait">
          <QuestionCard key={currentQ?.id} q={currentQ} lang={language} selected={answers[currentQ?.id]} onSelect={select} />
        </AnimatePresence>
        {submitting && (
          <div className="mt-6 text-center text-xs text-[#6F6B66] kt-tracking uppercase">Preparing result…</div>
        )}
        {(qIdx > 0 || phaseIdx > 0) && !submitting && (
          <div className="mt-10">
            <button onClick={back} data-testid="salon-female-back"
              className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">
              ← Back
            </button>
          </div>
        )}
      </div>
      <AnimatePresence>
        {showInsight && phase && (
          <InsightScreen
            phase={phase} lang={language}
            onContinue={() => { setShowInsight(false); setPhaseIdx(phaseIdx + 1); setQIdx(0); }}
            continueLabel="Continue"
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// -------------------- Male path (reuses MALE_QUESTIONS)
function MaleFlow({ language, onDone }) {
  const [answers, setAnswers] = useState({ gender_for_visual: "man" });
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const flow = useMemo(
    () => MALE_QUESTIONS.filter((q) => !q.condition || q.condition(answers)),
    [answers]
  );
  const q = flow[step];
  const total = flow.length;

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/salons/anonymous-assessment`, {
        flow: "male",
        language,
        answers: finalAnswers,
        confirm_adult_and_no_pregnancy: true,
      });
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compute result");
      setSubmitting(false);
    }
  };

  const advance = (next) => {
    if (step + 1 >= total) submit(next);
    else setStep(step + 1);
  };

  const pickSingle = (optId) => {
    const next = { ...answers, [q.id]: optId };
    setAnswers(next);
    setTimeout(() => advance(next), 220);
  };
  const toggleMulti = (optId) => {
    const cur = answers[q.id] || [];
    let next;
    if (optId === "none") next = ["none"];
    else next = cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur.filter((x) => x !== "none"), optId];
    setAnswers({ ...answers, [q.id]: next });
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed top-0 inset-x-0 z-40 bg-white/85 backdrop-blur border-b kt-line">
        <div className="max-w-md mx-auto px-6 py-3">
          <div className="flex items-center justify-between text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
            <span data-testid="male-progress-label">{step + 1}/{total} · 90-second check</span>
            <span>{Math.round(((step + 1) / total) * 100)}%</span>
          </div>
          <div className="h-1 w-full bg-[#F3F3F3] rounded-full overflow-hidden">
            <div className="h-full bg-[#A8AD94] transition-all duration-300" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 pt-24 pb-40">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-4">
          {q?.title[language] || q?.title.en}
        </div>
        {q?.type === "multi" ? (
          <div className="grid grid-cols-2 gap-3">
            {q.options.map((opt) => {
              const selected = (answers[q.id] || []).includes(opt.id);
              return (
                <button key={opt.id} data-testid={`male-multi-${opt.id}`} onClick={() => toggleMulti(opt.id)}
                  className={`rounded-2xl border kt-line p-3 bg-white text-left kt-shadow ${selected ? "ring-2 ring-[#D8C4A5]" : ""}`}>
                  <div className="text-sm text-[#383633]">{opt.label[language] || opt.label.en}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {q?.options.map((opt) => (
              <button key={opt.id} data-testid={`male-opt-${opt.id}`} onClick={() => pickSingle(opt.id)}
                className={`rounded-2xl border kt-line p-3 bg-white text-left kt-shadow ${answers[q.id] === opt.id ? "ring-2 ring-[#D8C4A5]" : ""}`}>
                <div className="h-20 rounded-xl mb-2 overflow-hidden bg-[#F3F3F3]">
                  {opt.image && <img src={opt.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                </div>
                <div className="text-sm text-[#383633]">{opt.label[language] || opt.label.en}</div>
              </button>
            ))}
          </div>
        )}
        {q?.type === "multi" && (
          <Button
            data-testid="male-multi-continue"
            onClick={() => advance(answers)}
            disabled={!(answers[q.id] && answers[q.id].length)}
            className="mt-6 w-full rounded-full bg-[#383633] hover:bg-[#4A4238] text-white h-12 text-xs kt-tracking uppercase">
            Continue
          </Button>
        )}
        {step > 0 && !submitting && (
          <div className="mt-8">
            <button data-testid="salon-male-back" onClick={() => setStep(step - 1)}
              className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633]">
              ← Back
            </button>
          </div>
        )}
        {submitting && (
          <div className="mt-6 text-center text-xs text-[#6F6B66] kt-tracking uppercase">Preparing result…</div>
        )}
      </div>
    </div>
  );
}

// -------------------- Salon Assessment shell
export default function SalonAssessment() {
  const nav = useNavigate();
  const [language, setLanguage] = useState("en");
  const [me, setMe] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [safetyCheck, setSafetyCheck] = useState(false);
  const [gender, setGender] = useState(null);

  useEffect(() => {
    axios.get(`${API}/salons/me`).then((r) => {
      if (r.data.subscription_status !== "active") {
        toast.error("Please activate your subscription first.");
        nav("/salon/dashboard");
      } else {
        setMe(r.data);
      }
    }).catch(() => nav("/salon/login"));
  }, [nav]);

  const onDone = (payload) => {
    // Store result in sessionStorage (memory-scoped; cleared when tab closes)
    sessionStorage.setItem("salon_result", JSON.stringify({ ...payload, at: Date.now() }));
    nav("/salon/result");
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center text-[#6F6B66]">…</div>;

  return (
    <div className="min-h-screen bg-white" data-testid="salon-assessment">
      <SalonHeader language={language} setLanguage={setLanguage} />

      {!confirmed && (
        <div className="max-w-md mx-auto px-6 pt-20 pb-24" data-testid="salon-safety-gate">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Hairtu · Client safety confirmation
          </div>
          <h1 className="font-serif text-3xl leading-tight text-[#383633] tracking-tight mb-3">
            {language === "it"
              ? "Prima di iniziare l'analisi del cliente"
              : "Before starting your client assessment"}
          </h1>
          <p className="text-[#6F6B66] text-sm mb-6 leading-relaxed">
            {language === "it"
              ? "KT Milano richiede questa conferma per ogni assessment in salone. La routine consigliata è cosmetica e brand-neutrale."
              : "KT Milano requires this confirmation for every in-salon assessment. The routine we recommend is cosmetic and brand-neutral."}
          </p>
          <label className="flex items-start gap-3 bg-[#F8F6F2] border kt-line rounded-2xl p-4 cursor-pointer">
            <Checkbox
              checked={safetyCheck}
              onCheckedChange={(v) => setSafetyCheck(!!v)}
              data-testid="salon-safety-checkbox"
              className="mt-1 border-[#6F6B66] data-[state=checked]:bg-[#383633] data-[state=checked]:border-[#383633]"
            />
            <span className="text-sm text-[#383633] leading-relaxed">
              {language === "it"
                ? "Confermo che il cliente ha 18 anni o più e non è in gravidanza o allattamento."
                : "I confirm this client is 18 years or older and is not pregnant or breastfeeding."}
            </span>
          </label>
          <Button
            data-testid="salon-safety-continue"
            disabled={!safetyCheck}
            onClick={() => setConfirmed(true)}
            className="mt-6 w-full rounded-full bg-[#383633] hover:bg-[#4A4238] text-white h-12 text-xs kt-tracking uppercase disabled:opacity-40"
          >
            {language === "it" ? "Conferma e continua" : "Confirm and continue"}
          </Button>
          <div className="mt-6 text-[11px] text-[#6F6B66] leading-relaxed">
            HAIRTU — Powered by KT MILANO · Professional Italian Haircare
          </div>
        </div>
      )}

      {confirmed && !gender && (
        <div className="max-w-md mx-auto px-6 pt-20 pb-24">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3 flex items-center gap-1.5">
            <Scissors className="h-3.5 w-3.5" /> Hairtu · New client
          </div>
          <h1 className="font-serif text-4xl leading-tight text-[#383633] tracking-tight mb-2">
            {SALON_GENDER.title[language] || SALON_GENDER.title.en}
          </h1>
          <p className="text-[#6F6B66] text-sm mb-8">
            {SALON_GENDER.helper[language] || SALON_GENDER.helper.en}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {SALON_GENDER.options.map((opt) => (
              <button
                key={opt.id}
                data-testid={`salon-gender-${opt.id}`}
                onClick={() => setGender(opt.id)}
                className="rounded-2xl border kt-line bg-white kt-shadow overflow-hidden text-left hover:ring-2 hover:ring-[#D8C4A5] transition"
              >
                <div className="h-40 bg-[#F3F3F3]">
                  <img src={opt.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="p-3 flex items-center justify-between">
                  <span className="text-sm text-[#383633]">{opt.label[language] || opt.label.en}</span>
                  <Check className="h-4 w-4 text-[#A8AD94] opacity-0" />
                </div>
              </button>
            ))}
          </div>
          <div className="mt-10 text-[11px] text-[#6F6B66] leading-relaxed">
            HAIRTU — Powered by KT MILANO · Professional Italian Haircare
          </div>
        </div>
      )}

      {gender === "woman" && <FemaleFlow language={language} onDone={onDone} />}
      {gender === "man" && <MaleFlow language={language} onDone={onDone} />}
    </div>
  );
}
